import React, { useState, useContext, useMemo } from 'react';
import * as Yup from 'yup';
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import allForm from '../../Stateless/Form';
import { HimmaContext } from '../../../context/HimmaContext';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useAllMerchant } from '../../Query/MerchantQuery';
import { useAllDriverManagement } from '../../Query/DriverManagerQuery';
import { useGetAllCustomer } from '../../Query/CustomerQuery';
import { COLORS } from '../../../theme';
const validationSchema = Yup.object().shape({
  reportType: Yup.string().required('Report Type is required'),
  fromDate: Yup.string().when('reportType', {
    is: 'custom',
    then: (schema) => schema.required('From Date is required for custom date range'),
    otherwise: (schema) => schema.nullable(),
  }),
  toDate: Yup.string().when('reportType', {
    is: 'custom',
    then: (schema) => schema.required('To Date is required for custom date range'),
    otherwise: (schema) => schema.nullable(),
  }),
  storeType: Yup.array().of(Yup.string()).nullable(),
  storeOwnerIds: Yup.array().of(Yup.string()).nullable(),
  storeIds: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one store must be selected to download sales report')
    .required('At least one store must be selected to download sales report'),
  orderStatuses: Yup.array().of(Yup.string()).nullable(),
  paymentMethods: Yup.array().of(Yup.string()).nullable(),
  driverIds: Yup.array().of(Yup.string()).nullable(),
  customerIds: Yup.array().of(Yup.string()).nullable(),
});

const RestaurantEarningReports: React.FC = () => {
  const api = useContext(HimmaContext)!;
  const [selectedFromDate, setSelectedFromDate] = useState<Dayjs | null>(dayjs());
  const [selectedToDate, setSelectedToDate] = useState<Dayjs | null>(dayjs());
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | undefined>(undefined);

  // Master data for filters
  const { data: storesData } = useAllStores({
    page: 1,
    limit: 100,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    statusFilter: 'All',
    addedByFilter: 'All',
    merchantId: selectedMerchantId,
  });

  const { data: merchantData } = useAllMerchant({ initialData: {} });

  const { data: driverData } = useAllDriverManagement({
    page: 1,
    limit: 100,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  } as any);

  const { data: customerData } = useGetAllCustomer(
    {
      page: 1,
      limit: 100,
      sortBy: 'name',
      sortOrder: 'asc',
    } as any
  );

  const storeOptions = useMemo(
    () =>
      (storesData?.data || []).map((store: any) => ({
        value: store.id,
        label:
          store.restaurantDetails?.storeName ||
          store.nameEnglish ||
          store.name ||
          'Unknown Store',
        merchantId: store.merchantId,
      })),
    [storesData]
  );

  const storeOwnerOptions = useMemo(
    () =>
      (merchantData ?? []).map((merchant: any) => {
        const name = `${merchant.firstName ?? ''} ${merchant.lastName ?? ''}`.trim() ||
          merchant.name ||
          'Unknown Owner';
        const email = merchant.email || '';
        return {
          value: merchant.id,
          label: email ? `${name} (${email})` : name,
        };
      }),
    [merchantData]
  );

  const driverOptions = useMemo(
    () =>
      (driverData?.data || []).map((item: any) => {
        const name = `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim() ||
          'Unknown Driver';
        const email = item.email || '';
        return {
          value: item.deliverBoyId,
          label: email ? `${name} (${email})` : name,
        };
      }),
    [driverData]
  );

  const customerOptions = useMemo(
    () =>
      (customerData?.data || []).map((item: any) => {
        const name = item.name || item.userName || 'Unknown Customer';
        const email = item.email || item.userEmail || '';
        return {
          value: item.userId,
          label: email ? `${name} (${email})` : name,
        };
      }),
    [customerData]
  );

  const reportTypeOptions = [
    { value: 'daily', label: 'Today' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'custom', label: 'Custom' },
  ];

  const storeTypeOptions = [
    { value: 'Restaurant', label: 'Restaurant' },
    { value: 'GroceryStore', label: 'Grocery' },
    { value: 'PharmacyStore', label: 'Pharmacy' },
  ];

  const orderStatusOptions = [
    { value: 'ORDER_DELIVERED', label: 'Delivered' },
    { value: 'ORDER_CANCELLED', label: 'Cancelled' },
    { value: 'MERCHANT_CANCELLED', label: 'Merchant Cancelled' },
    { value: 'DELIVERY_FAILED', label: 'Delivery Failed' },
  ];

  const paymentMethodOptions = [
    { value: 'cashOnDelivery', label: 'Cash' },
    { value: 'onlinePayment', label: 'Online' },
    { value: 'wallet', label: 'Wallet' },
    { value: 'walletAndCashOnDelivery', label: 'Wallet + Cash' },
    { value: 'walletAndOnlinePayment', label: 'Wallet + Online' },
  ];

  const formConfig = {
    initialValues: {
      reportType: 'custom',
      fromDate: dayjs().toISOString(),
      toDate: dayjs().toISOString(),
      storeType: [],
      storeOwnerIds: [],
      storeIds: [],
      orderStatuses: [],
      paymentMethods: [],
      driverIds: [],
      customerIds: [],
    },
    validationSchema,
    onSubmit: async (values: Record<string, any>) => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        setInfoMessage(null);

        const {
          reportType,
          storeIds,
          storeOwnerIds,
          storeType,
          orderStatuses,
          paymentMethods,
          driverIds,
          customerIds,
        } = values;

        const fromDate = selectedFromDate ? selectedFromDate?.toISOString().split('T')[0] + 'T00:00:00Z' : undefined;
        const toDate = selectedToDate ? selectedToDate?.toISOString().split('T')[0] + 'T23:59:59Z' : undefined;


        // Validate that reportType is selected
        if (!reportType || reportType.trim() === '') {
          setErrorMessage('Report Type is required');
          setIsLoading(false);
          return;
        }

        if (!Array.isArray(storeIds) || storeIds.length === 0) {
          setErrorMessage('At least one store must be selected to download sales report');
          setIsLoading(false);
          return;
        }

        // Map UI report type to API-supported dateRange enum
        const reportTypeToDateRangeMap: Record<string, string> = {
          daily: 'today',
          weekly: 'thisWeek',
          monthly: 'thisMonth',
          custom: 'custom',
        };

        const dateRange =
          reportTypeToDateRangeMap[reportType] ?? 'custom';
        
        // Ensure dateRange is valid
        if (!dateRange || dateRange.trim() === '') {
          setErrorMessage('Invalid report type selected');
          setIsLoading(false);
          return;
        }

        // Build the request object according to SalesReportRequest schema
        const salesReportRequest: any = {
          dateRange,
          storeIds: storeIds.length > 0 ? storeIds : undefined,
          merchantId:
            Array.isArray(storeOwnerIds) && storeOwnerIds.length > 0
              ? storeOwnerIds[0]
              : undefined,
          storeType:
            Array.isArray(storeType) && storeType.length > 0 ? storeType : undefined,
          orderStatuses:
            Array.isArray(orderStatuses) && orderStatuses.length > 0
              ? orderStatuses
              : undefined,
          paymentMethods:
            Array.isArray(paymentMethods) && paymentMethods.length > 0
              ? paymentMethods
              : undefined,
          driverIds:
            Array.isArray(driverIds) && driverIds.length > 0 ? driverIds : undefined,
          customerIds:
            Array.isArray(customerIds) && customerIds.length > 0
              ? customerIds
              : undefined,
        };

        // Add dateFrom and dateTo only if dateRange is 'custom'
        if (dateRange === 'custom') {
          // Validate that both dates are provided for custom date range
          if (!fromDate || fromDate.trim() === '') {
            setErrorMessage('From Date is required for custom date range');
            setIsLoading(false);
            return;
          }
          if (!toDate || toDate.trim() === '') {
            setErrorMessage('To Date is required for custom date range');
            setIsLoading(false);
            return;
          }
          salesReportRequest.dateFrom = fromDate;
          salesReportRequest.dateTo = toDate;
        }

        const response = await api.downloadSalesReport(salesReportRequest, {
          responseType: 'blob' as any,
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sales-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setSuccessMessage('Report downloaded successfully!');
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (error: any) {
        const statusCode = error?.response?.status;
        let errorMsg = '';
        
        // Try to parse error message from different response formats
        if (error?.response?.data) {
          // When responseType is 'blob', error responses might be Blob objects
          if (error.response.data instanceof Blob) {
            // Convert blob to text and parse JSON
            try {
              const text = await error.response.data.text();
              const parsed = JSON.parse(text);
              errorMsg = parsed.message || parsed.error || text;
            } catch (parseError) {
              // If parsing fails, try to get text anyway
              try {
                errorMsg = await error.response.data.text();
              } catch {
                errorMsg = 'Failed to download sales report. Please try again.';
              }
            }
          } else if (typeof error.response.data === 'string') {
            // If it's already a string, try to parse as JSON
            try {
              const parsed = JSON.parse(error.response.data);
              errorMsg = parsed.message || parsed.error || error.response.data;
            } catch {
              errorMsg = error.response.data;
            }
          } else if (error.response.data.message) {
            // If it's already an object with message
            errorMsg = error.response.data.message;
          } else if (error.response.data.error) {
            errorMsg = error.response.data.error;
          }
        }
        
        // Fallback to default error message
        if (!errorMsg) {
          errorMsg = error?.message || 'Failed to download sales report. Please try again.';
        }
        
        // Check if it's a 404 with "No data" message - show as info instead of error
        if (
          statusCode === 404 &&
          (errorMsg?.toLowerCase().includes('no data') ||
           errorMsg?.toLowerCase().includes('not found') ||
           errorMsg?.toLowerCase().includes('no sales') ||
           errorMsg?.toLowerCase().includes('matching the selected filters'))
        ) {
          // Show as info message instead of error
          setInfoMessage(errorMsg || 'No sales data found matching the selected filters and date range.');
          setTimeout(() => setInfoMessage(null), 5000);
        } else {
          // Show as error for other cases
          setErrorMessage(errorMsg);
        }
        console.error('Failed to download sales report', error);
      } finally {
        setIsLoading(false);
      }
    },
    validateOnChange: true,
    enableReinitialize: true,
    fields: [
      // Row 1: Report Type | Store Type
      {
        name: 'reportType',
        label: 'Report Type:',
        type: 'custom',
        component: ({ field, form }: any) => {
          // Clear date fields when switching away from custom
          const handleChange = (event: any) => {
            const newValue = event.target.value;
            form.setFieldValue(field.name, newValue);
            if (newValue !== 'custom') {
              form.setFieldValue('fromDate', '');
              form.setFieldValue('toDate', '');
              setSelectedFromDate(null);
              setSelectedToDate(null);
            }
          };
          
          return (
            <TextField
              {...field}
              select
              SelectProps={{
                native: true,
              }}
              onChange={handleChange}
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  fontSize: 14,
                  minHeight: 56,
                },
              }}
            >
              <option value="">Select Report Type</option>
              {reportTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
          );
        },
        isRequired: true,
      },
      
      // Row 2: From Date | To Date
      {
        name: 'fromDate',
        label: 'From Date:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const reportType = form.values.reportType;
          const isCustom = reportType === 'custom';
          const hasError = form.touched.fromDate && !!form.errors.fromDate;
          
          if (reportType === 'daily') {
            return null;
          }
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedFromDate}
                onChange={(newValue: Dayjs | null) => {
                  setSelectedFromDate(newValue);
                  form.setFieldValue(field.name,newValue ? newValue.toISOString() : '');
                }}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    placeholder: 'Select start date',
                    helperText: hasError ? form.errors.fromDate : (isCustom ? 'Required for custom date range' : 'Optional'),
                    error: hasError,
                    required: isCustom,
                    onBlur: () => {
                      form.setFieldTouched(field.name, true);
                    },
                    sx: {
                      width: '100%',
                      '& .MuiInputBase-root': {
                        minHeight: 56,
                      },
                    },
                  },
                }}
                format="MM/DD/YYYY"
              />
            </LocalizationProvider>
          );
        },
        sx: (values: any) => ({
          display: values.reportType === 'daily' ? 'none' : 'block',
        }),
      },
      {
        name: 'toDate',
        label: 'To Date:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const reportType = form.values.reportType;
          const isCustom = reportType === 'custom';
          const hasError = form.touched.toDate && !!form.errors.toDate;
          
          if (reportType === 'daily') {
            return null;
          }
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedToDate}
                onChange={(newValue: Dayjs | null) => {
                  setSelectedToDate(newValue);
                  form.setFieldValue(
                    field.name,
                    newValue ? newValue.toISOString() : ''
                  );
                }}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    placeholder: 'Select end date',
                    helperText: hasError ? form.errors.toDate : (isCustom ? 'Required for custom date range' : 'Optional'),
                    error: hasError,
                    required: isCustom,
                    onBlur: () => {
                      form.setFieldTouched(field.name, true);
                    },
                    sx: {
                      width: '100%',
                      '& .MuiInputBase-root': {
                        minHeight: 56,
                      },
                    },
                  },
                }}
                format="MM/DD/YYYY"
              />
            </LocalizationProvider>
          );
        },
        sx: (values: any) => ({
          display: values.reportType === 'daily' ? 'none' : 'block',
        }),
      },
      {
        name: 'storeType',
        label: 'Store Type:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = storeTypeOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={storeTypeOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select store types (optional)"
                    helperText="Leave empty to include all types"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      // Row 3: Store Owner | Stores
      {
        name: 'storeOwnerIds',
        label: 'Store Owner:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = storeOwnerOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
            setSelectedMerchantId(newValue[0] ?? undefined);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={storeOwnerOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) => {
                  const ownerIds = newValue.map((v: any) => v.value);
                  form.setFieldValue(field.name, ownerIds);
                  setSelectedMerchantId(ownerIds[0] ?? undefined);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select store owners (optional)"
                    helperText="Filter by merchant/owner"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      // Row 4: Order Status | Payment Method
      {
        name: 'storeIds',
        label: 'Stores:',
        type: 'customWithError',
        isRequired: true,
        component: ({ field, form }: any) => {
          const selectedValues = storeOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          const hasOwnerSelection =
            Array.isArray(form.values.storeOwnerIds) && form.values.storeOwnerIds.length > 0;
          const hasError = form.touched.storeIds && !!form.errors.storeIds;
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
            // Clear error if no choices are selected
            if (newValue.length === 0) {
              form.setFieldError(field.name, undefined);
            }
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                disabled={!hasOwnerSelection}
                options={storeOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) => {
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  );
                  // Clear error when user makes a selection
                  if (newValue.length > 0) {
                    form.setFieldError(field.name, undefined);
                  }
                }}
                onBlur={() => {
                  form.setFieldTouched(field.name, true);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select stores (required)"
                    helperText={
                      hasError
                        ? undefined
                        : hasOwnerSelection
                        ? 'At least one store must be selected'
                        : 'Select a store owner first'
                    }
                    error={hasError}
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        name: 'orderStatuses',
        label: 'Order Status:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = orderStatusOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={orderStatusOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select order statuses (optional)"
                    helperText="Leave empty to include all statuses"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      // Row 5: Driver Name | Customer Name
      {
        name: 'paymentMethods',
        label: 'Payment Method:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = paymentMethodOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={paymentMethodOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select payment methods (optional)"
                    helperText="Leave empty to include all methods"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        name: 'driverIds',
        label: 'Driver Name:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = driverOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={driverOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select drivers (optional)"
                    helperText="Filter by delivery drivers"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        name: 'customerIds',
        label: 'Customer Name:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = customerOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={customerOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select customers (optional)"
                    helperText="Filter by specific customers"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
    ],
    customButton: () => (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4, mb: 2 }}>
        <Button
          type="reset"
          variant="outlined"
          disabled={isLoading}
          sx={{
            borderColor: COLORS.secondary.main,
            color: COLORS.secondary.main,
            minWidth: 120,
            textTransform: 'none',
            fontSize: 16,
            fontWeight: 500,
            py: 1.5,
            '&:hover': {
              borderColor: COLORS.secondary.main,
              backgroundColor: alpha(COLORS.secondary.main, 0.1),
            },
          }}
        >
          Reset
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          sx={{
            backgroundColor: COLORS.primary.main,
            minWidth: 150,
            textTransform: 'none',
            fontSize: 16,
            fontWeight: 600,
            py: 1.5,
            '&:hover': {
              backgroundColor: COLORS.primary.dark,
            },
            '&:disabled': {
              backgroundColor: alpha('#ff6b35', 0.5),
            },
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} sx={{ color: 'white' }} />
              Generating...
            </Box>
          ) : (
            'Download Report'
          )}
        </Button>
      </Box>
    ),
  };

  return (
    <Box
      sx={{
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        padding: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1400, margin: '0 auto' }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            marginBottom: 1,
            color: '#1a1a1a',
            fontSize: { xs: '1.75rem', sm: '2rem' },
          }}
        >
          Sales Reports
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#666',
            marginBottom: 3,
            fontSize: '0.95rem',
          }}
        >
          Generate and download detailed sales reports with advanced filtering options
        </Typography>

        {(errorMessage || successMessage || infoMessage) && (
          <Box sx={{ mb: 3 }}>
            {errorMessage && (
              <Alert
                severity="error"
                onClose={() => setErrorMessage(null)}
                sx={{ mb: 2 }}
              >
                {errorMessage}
              </Alert>
            )}
            {successMessage && (
              <Alert
                severity="success"
                onClose={() => setSuccessMessage(null)}
                sx={{ mb: 2 }}
              >
                {successMessage}
              </Alert>
            )}
            {infoMessage && (
              <Alert
                severity="info"
                onClose={() => setInfoMessage(null)}
                sx={{ mb: 2 }}
              >
                {infoMessage}
              </Alert>
            )}
          </Box>
        )}

        <Paper
          elevation={2}
          sx={{
            padding: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2,
            backgroundColor: 'white',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#333',
                mb: 1,
                fontSize: '1.1rem',
              }}
            >
              Report Filters
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
              Configure your report parameters below. Fields marked with * are required.
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {allForm(formConfig)()}
        </Paper>
      </Box>
    </Box>
  );
};

export default RestaurantEarningReports;
